'use client';

import { useState, useEffect, useRef } from "react";

/**
 * "Built to give radiologists their time back" — the outcomes band.
 * Six pointers with icons. Numeric ones count up when the band scrolls into
 * view; word-value ones (modalities, templates) render statically. Cards lift
 * on hover. Matches the page's dynamic feel.
 */

const IconBolt = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13z" /></svg>
);
const IconLayers = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 12l9 5 9-5" /><path d="M3 16.5l9 5 9-5" /></svg>
);
const IconBrowser = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M7 6.5h.01M10 6.5h.01" /></svg>
);

// Prominent, on-brand animated illustrations — one per stat. Clean line-art,
// contained in a tinted panel, so it reads premium rather than flashy.
const MotifSpeed = () => (
  <svg viewBox="0 0 104 104" aria-hidden="true">
    <g className="dres-eq" fill="currentColor">
      <rect x="8"  y="38" width="12" height="28" rx="6" />
      <rect x="27" y="26" width="12" height="52" rx="6" />
      <rect x="46" y="32" width="12" height="40" rx="6" />
      <rect x="65" y="20" width="12" height="64" rx="6" />
      <rect x="84" y="36" width="12" height="32" rx="6" />
    </g>
  </svg>
);
const MotifModality = () => (
  <svg viewBox="0 0 104 104" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="52" cy="52" r="46" opacity="0.22" />
    <circle cx="52" cy="52" r="31" opacity="0.22" />
    <circle cx="52" cy="52" r="16" opacity="0.22" />
    <path d="M6 52 H98 M52 6 V98" opacity="0.14" />
    <g className="dres-sweep">
      <path d="M52 52 L52 6 A46 46 0 0 1 85 19 Z" fill="currentColor" stroke="none" opacity="0.18" />
      <line x1="52" y1="52" x2="52" y2="6" strokeWidth="2.5" />
    </g>
    <circle className="dres-blip" cx="72" cy="34" r="3.5" fill="currentColor" stroke="none" />
    <circle className="dres-blip dres-blip-2" cx="34" cy="66" r="3" fill="currentColor" stroke="none" />
  </svg>
);
const MotifFootprint = () => (
  <svg viewBox="0 0 104 104" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="12" y="20" width="80" height="64" rx="10" opacity="0.5" />
    <path d="M12 38 H92" opacity="0.5" />
    <circle cx="22" cy="29" r="2" fill="currentColor" stroke="none" />
    <circle cx="30" cy="29" r="2" fill="currentColor" stroke="none" />
    <circle cx="38" cy="29" r="2" fill="currentColor" stroke="none" />
    <rect className="dres-line" x="24" y="50" width="40" height="5" rx="2.5" fill="currentColor" stroke="none" />
    <rect className="dres-line dres-line-2" x="24" y="61" width="56" height="5" rx="2.5" fill="currentColor" stroke="none" />
    <rect className="dres-line dres-line-3" x="24" y="72" width="34" height="5" rx="2.5" fill="currentColor" stroke="none" />
  </svg>
);

// type 'num' → counts up to `target`; type 'text' → static `text` value.
const STATS = [
  { Icon: IconBolt,    Motif: MotifSpeed,     type: "num",  prefix: "<", target: 5,    suffix: "s",         label: "From dictation to a structured first draft" },
  { Icon: IconLayers,  Motif: MotifModality,  type: "text", text: "All",  suffix: " modality",              label: "CT, MRI, USG, mammo, X-ray and more" },
  { Icon: IconBrowser, Motif: MotifFootprint, type: "text", text: "Zero", suffix: " footprint",             label: "No installs — runs in your browser" },
];

const DURATION = 1100; // count-up duration (ms)
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

function useCountUp(stats) {
  const [nums, setNums] = useState(() => stats.map((s) => (s.type === "num" ? 0 : null)));
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const begin = () => {
      if (started.current) return;
      started.current = true;
      setInView(true);
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / DURATION, 1);
        const e = easeOut(t);
        setNums(stats.map((s) => (s.type === "num" ? Math.round(s.target * e) : null)));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) begin();
    }, { threshold: 0, rootMargin: "0px 0px -12% 0px" });
    io.observe(el);
    // Safety net: never leave the content hidden if the observer doesn't fire.
    const fallback = setTimeout(begin, 1800);
    return () => { io.disconnect(); clearTimeout(fallback); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [nums, ref, inView];
}

export default function DxrResults() {
  const [nums, ref, inView] = useCountUp(STATS);

  return (
    <section className={`dres${inView ? " is-inview" : ""}`} ref={ref}>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <h2 className="dres-h2">Built to give radiologists their time back</h2>
      <p className="dres-sub">
        DXR removes the typing and formatting tax so you can spend your attention on the read, not the paperwork.
      </p>
      <div className="dres-grid">
        {STATS.map((s, i) => (
          <div key={s.label} className="dres-card">
            <div className="dres-text">
              <div className="dres-value" suppressHydrationWarning>
                {s.type === "num" ? `${s.prefix}${nums[i]}` : s.text}
                <span className="dres-suffix">{s.suffix}</span>
              </div>
              <div className="dres-label">{s.label}</div>
            </div>
            <div className="dres-viz" aria-hidden="true"><s.Motif /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

const styles = `
.dres { width: 90vw; margin: 0 auto; padding: 4% 0; font-family: 'Inter', sans-serif; }
.dres-h2 { font-size: clamp(26px, 3.6vw, 40px); font-weight: 700; color: #fff; text-align: center; letter-spacing: -0.02em; margin: 0 0 10px; }
.dres-sub { font-size: 16px; line-height: 1.6; color: rgba(255,255,255,0.7); text-align: center; max-width: 640px; margin: 0 auto 40px; }
.dres-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.dres-card { position: relative; overflow: hidden; display: flex; align-items: center; justify-content: space-between; gap: 16px; text-align: left; background: rgba(255,255,255,0.024); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 26px 28px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
  opacity: 0; translate: 0 16px;
  transition-property: opacity, translate, transform, border-color, background;
  transition-duration: 0.55s, 0.55s, 0.22s, 0.22s, 0.22s;
  transition-timing-function: cubic-bezier(0.22,1,0.36,1), cubic-bezier(0.22,1,0.36,1), ease, ease, ease; }
.dres.is-inview .dres-card { opacity: 1; translate: 0 0; }
.dres.is-inview .dres-card:nth-child(2) { transition-delay: 0.1s, 0.1s, 0s, 0s, 0s; }
.dres.is-inview .dres-card:nth-child(3) { transition-delay: 0.2s, 0.2s, 0s, 0s, 0s; }
.dres-card:hover { border-color: rgba(41,151,255,0.3); background: rgba(255,255,255,0.04); transform: translateY(-3px); }

.dres-text { position: relative; z-index: 1; min-width: 0; }
.dres-value { font-size: clamp(34px, 3.4vw, 46px); font-weight: 600; color: #fff; line-height: 1; margin-bottom: 12px; letter-spacing: -0.035em; font-variant-numeric: tabular-nums; }
.dres-suffix { font-size: 0.42em; font-weight: 500; color: rgba(255,255,255,0.42); margin-left: 3px; letter-spacing: -0.01em; }
.dres-label { font-size: 13.5px; line-height: 1.55; color: rgba(255,255,255,0.55); max-width: 190px; }

/* prominent on-brand animated illustration panel */
.dres-viz { flex: none; width: 104px; height: 104px; display: flex; align-items: center; justify-content: center; border-radius: 14px; background: rgba(41,151,255,0.07); border: 1px solid rgba(41,151,255,0.16); color: #2997FF; transition: background 220ms ease, border-color 220ms ease; }
.dres-viz svg { width: 82px; height: 82px; display: block; }
.dres-card:hover .dres-viz { background: rgba(41,151,255,0.11); border-color: rgba(41,151,255,0.3); }

/* Illustrations stay still at rest and come alive on hover (calm, premium). */
/* speed — waveform */
.dres-eq rect { transform-box: fill-box; transform-origin: center; }
.dres-card:hover .dres-eq rect { animation: dresEq 1.4s ease-in-out infinite; }
.dres-card:hover .dres-eq rect:nth-child(2) { animation-delay: 0.15s; }
.dres-card:hover .dres-eq rect:nth-child(3) { animation-delay: 0.3s; }
.dres-card:hover .dres-eq rect:nth-child(4) { animation-delay: 0.45s; }
.dres-card:hover .dres-eq rect:nth-child(5) { animation-delay: 0.6s; }
@keyframes dresEq { 0%, 100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }

/* modality — radar sweep */
.dres-sweep { transform-box: view-box; transform-origin: 52px 52px; }
.dres-card:hover .dres-sweep { animation: dresSweep 2.8s linear infinite; }
@keyframes dresSweep { to { transform: rotate(360deg); } }
.dres-card:hover .dres-blip { animation: dresBlip 2.8s ease-in-out infinite; }
.dres-card:hover .dres-blip-2 { animation-delay: 1.1s; }
@keyframes dresBlip { 0%, 70%, 100% { opacity: 0.2; } 82% { opacity: 1; } }

/* footprint — instant content load */
.dres-line { transform-box: fill-box; transform-origin: left center; }
.dres-card:hover .dres-line { animation: dresLine 3.2s ease-in-out infinite; }
.dres-card:hover .dres-line-2 { animation-delay: 0.22s; }
.dres-card:hover .dres-line-3 { animation-delay: 0.44s; }
@keyframes dresLine { 0%, 10% { transform: scaleX(0); opacity: 0; } 28%, 80% { transform: scaleX(1); opacity: 1; } 96%, 100% { transform: scaleX(0); opacity: 0; } }

@media (prefers-reduced-motion: reduce) {
  .dres-card { opacity: 1; translate: none; transition: none; }
  .dres-card:hover .dres-eq rect,
  .dres-card:hover .dres-sweep,
  .dres-card:hover .dres-blip,
  .dres-card:hover .dres-line { animation: none; }
}

@media (max-width: 1200px) {
  .dres-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 560px) {
  .dres-grid { grid-template-columns: 1fr; }
  .dres-card { padding: 22px 24px; }
}
`;
